import { useEffect, useMemo, useState } from "react";
import {
  STEP_TO_INDEX,
  STEPS,
  useOnBoardingStepManager,
} from "./useOnboardingStepManager";
import { useParams } from "react-router-dom";
import {
  useAddProgramMutation,
  useGetAcademicYearsByOrgLazyQuery,
  useGetGraduationClassByOrgLazyQuery,
  useGetOrganizationByIdLazyQuery,
  useGetProgramsByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { Organization } from "types/organization";
import { AcademicYear } from "types/academicYear";
import { GraduationClass } from "types/graduationClass";

export const useOnboarding = () => {
  const {
    state,
    dispatch,
    previousStep,
    nextStep,
    goToStep: goToStepManager,
  } = useOnBoardingStepManager();
  const { organizationId } = useParams();

  const [
    getOrganization,
    { data: organizationData, loading: organizationLoading },
  ] = useGetOrganizationByIdLazyQuery();
  const [
    getAcademicYears,
    { data: academicYearsData, loading: academicYearsLoading },
  ] = useGetAcademicYearsByOrgLazyQuery();
  const [getProgram, { data: programData, loading: programLoading }] =
    useGetProgramsByOrgLazyQuery();
  const [
    getGraduationClass,
    { data: graduationClassData, loading: graduationClassLoading },
  ] = useGetGraduationClassByOrgLazyQuery();
  const [addProgram] = useAddProgramMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [academicYearId, setAcademicYearId] = useState<string | null>(null);
  const [graduationClassId, setGraduationClassId] = useState<string | null>(
    null,
  );
  const [studentsAdded, setStudentsAdded] = useState<string[]>([]);

  useEffect(() => {
    const addNewProgram = async () => {
      await addProgram({ variables: { orgId: organizationId } });
      await getProgram({ variables: { orgId: organizationId } });
    };
    if (
      !programLoading &&
      programData?.program?.length === 0 &&
      organizationId
    ) {
      addNewProgram();
    }
  }, [programLoading, programData, organizationId]);

  useEffect(() => {
    if (
      !organizationLoading &&
      !academicYearsLoading &&
      !programLoading &&
      !graduationClassLoading
    ) {
      setIsLoading(false);
    }
  }, [
    organizationLoading,
    academicYearsLoading,
    programLoading,
    graduationClassLoading,
  ]);

  const organization = useMemo(
    () => (organizationData?.organization_by_pk as Organization) || null,
    [organizationData],
  );

  const programId = useMemo(
    () => (programData?.program?.[0]?.program_id as string) || null,
    [programData],
  );

  const graduationClass = useMemo(() => {
    if (graduationClassData?.graduation_class?.[0]) {
      setGraduationClassId(
        graduationClassData.graduation_class[0].graduation_class_id,
      );
      return graduationClassData.graduation_class[0] as GraduationClass;
    }
    return null;
  }, [graduationClassData]);

  const academicYear = useMemo(() => {
    if (
      academicYearsData?.program &&
      academicYearsData?.program.length > 0 &&
      academicYearsData?.program[0].academic_years.length > 0
    ) {
      const academicYear = academicYearsData?.program[0].academic_years[0];
      setAcademicYearId(academicYear.academic_year_id);
      return {
        ...academicYear,
        tags: academicYear.tags?.map((t) => t.tag),
        requirements: academicYear.requirements.map((r) => r.requirement),
      } as AcademicYear;
    }
    return null;
  }, [academicYearsData]);

  useEffect(() => {
    if (organizationId) {
      getOrganization({ variables: { organizationId } });
      getAcademicYears({ variables: { orgId: organizationId } });
      getProgram({ variables: { orgId: organizationId } });
      getGraduationClass({ variables: { orgId: organizationId } });
    }
  }, [organizationId]);

  useEffect(() => {
    if (graduationClass) {
      goToStepManager(STEPS.ADD_STUDENTS);
    } else if (academicYear) {
      goToStepManager(STEPS.GRADUATION_CLASS);
    } else if (organization && programId) {
      goToStepManager(STEPS.ACADEMIC_YEAR);
    }
  }, [isLoading, organization, programId, academicYear, graduationClass]);

  const onYearlyCurriculumCreated = (academicYearId: string) => {
    setAcademicYearId(academicYearId);
    getAcademicYears({ variables: { orgId: organizationId } });
  };

  const onGraduationClassCreated = (graduationClassId: string) => {
    setGraduationClassId(graduationClassId);
    getGraduationClass({ variables: { orgId: organizationId } });
  };

  const goToStep = (step: string) => {
    if (STEP_TO_INDEX[step] < STEP_TO_INDEX[state.currentStep]) {
      goToStepManager(step);
    }
  };

  return {
    state,
    organization,
    academicYear,
    programId,
    isLoading,
    academicYearId,
    graduationClass,
    graduationClassId,
    studentsAdded,
    goToStep,
    onYearlyCurriculumCreated,
    onGraduationClassCreated,
    setAcademicYearId,
    setGraduationClassId,
    setStudentsAdded,
    dispatch,
    previousStep,
    nextStep,
  };
};
